import React, { useState, useEffect } from 'react';
import { X, Send, Mail, MessageCircle, Image, Link, Video, FileText, Upload, Trash2 } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db, storage, functions } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

export const GuestMessagingModal = ({ bookId, selectedGuests, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [method, setMethod] = useState('email'); // 'email', 'whatsapp', 'both'
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);

  useEffect(() => {
    loadEventData();
  }, [bookId]);

  const loadEventData = async () => {
    try {
      const bookDoc = await getDoc(doc(db, 'pb-books', bookId));
      if (bookDoc.exists()) {
        const book = bookDoc.data();
        setEventData(book);
        
        // Generate default message
        const defaultSubject = `Update: ${book.title}`;
        const defaultMessage = generateDefaultMessage(book, selectedGuests);
        
        setSubject(defaultSubject);
        setMessage(defaultMessage);
      }
    } catch (error) {
      console.error('Error loading event data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateDefaultMessage = (book, guests) => {
    const eventSettings = book.eventSettings || {};
    const eventDate = eventSettings.eventDate ? 
      new Date(eventSettings.eventDate).toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }) : 'Date TBA';

    return `Hi {{name}},

We wanted to reach out with some important information about ${book.title}.

Event Details:
📅 Date: ${eventDate}
${eventSettings.eventTime ? `⏰ Time: ${eventSettings.eventTime}` : ''}
${eventSettings.venue ? `📍 Location: ${eventSettings.venue}` : ''}

${book.description || ''}

We look forward to seeing you there!

Best regards,
${eventSettings.organizerName || book.author || 'Event Organizer'}`;
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const timestamp = Date.now();
        const fileName = `pb-${bookId}/guest-messages/${timestamp}_${file.name}`;
        const storageRef = ref(storage, fileName);
        
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        
        setMedia(prev => [...prev, {
          type: getMediaType(file.type),
          url,
          name: file.name,
          size: file.size
        }]);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const getMediaType = (mimeType) => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType === 'application/pdf') return 'pdf';
    return 'file';
  };

  const addLink = () => {
    if (!linkUrl) return;
    
    setMedia(prev => [...prev, {
      type: 'link',
      url: linkUrl,
      text: linkText || linkUrl
    }]);
    
    setLinkUrl('');
    setLinkText('');
    setShowLinkInput(false);
  };

  const removeMedia = (index) => {
    setMedia(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!message.trim()) {
      alert('Please enter a message');
      return;
    }

    const confirmed = window.confirm(
      `Send message to ${selectedGuests.length} guest${selectedGuests.length > 1 ? 's' : ''} via ${method}?`
    );

    if (!confirmed) return;

    setSending(true);
    try {
      const sendGuestMessage = httpsCallable(functions, 'sendGuestMessage');
      const result = await sendGuestMessage({
        bookId,
        guestIds: selectedGuests.map(g => g.id),
        method,
        subject,
        message,
        media
      });

      alert(`✓ Sent: ${result.data.success}, ✗ Failed: ${result.data.failed}`);
      onClose();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send messages: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Send Message to Guests</h2>
            <p className="text-sm text-gray-500 mt-1">
              {selectedGuests.length} guest{selectedGuests.length > 1 ? 's' : ''} selected
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Delivery Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delivery Method
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setMethod('email')}
                className={`flex-1 p-4 border-2 rounded-lg transition ${
                  method === 'email'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Mail className="w-6 h-6 mx-auto mb-2 text-emerald-600" />
                <p className="font-medium">Email</p>
              </button>
              
              <button
                onClick={() => setMethod('whatsapp')}
                className={`flex-1 p-4 border-2 rounded-lg transition ${
                  method === 'whatsapp'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <MessageCircle className="w-6 h-6 mx-auto mb-2 text-emerald-600" />
                <p className="font-medium">WhatsApp</p>
              </button>
              
              <button
                onClick={() => setMethod('both')}
                className={`flex-1 p-4 border-2 rounded-lg transition ${
                  method === 'both'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Send className="w-6 h-6 mx-auto mb-2 text-emerald-600" />
                <p className="font-medium">Both</p>
              </button>
            </div>
          </div>

          {/* Subject (Email only) */}
          {(method === 'email' || method === 'both') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Message subject..."
              />
            </div>
          )}

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
              <span className="text-xs text-gray-500 ml-2">Use {'{{name}}'} for guest name</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={12}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent font-mono text-sm"
              placeholder="Your message here..."
            />
          </div>

          {/* Media Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attachments & Links
            </label>
            
            {/* Media List */}
            {media.length > 0 && (
              <div className="space-y-2 mb-3">
                {media.map((item, index) => (
                  <MediaItem
                    key={index}
                    item={item}
                    onRemove={() => removeMedia(index)}
                  />
                ))}
              </div>
            )}

            {/* Add Media Buttons */}
            <div className="flex gap-2">
              <label className="flex-1 cursor-pointer">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <div className="px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition text-center">
                  <Upload className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                  <p className="text-sm text-gray-600">
                    {uploading ? 'Uploading...' : 'Upload File'}
                  </p>
                </div>
              </label>

              <button
                onClick={() => setShowLinkInput(!showLinkInput)}
                className="flex-1 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition"
              >
                <Link className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                <p className="text-sm text-gray-600">Add Link</p>
              </button>
            </div>

            {/* Link Input */}
            {showLinkInput && (
              <div className="mt-3 p-4 bg-gray-50 rounded-lg space-y-3">
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Link text (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                <div className="flex gap-2">
                  <button
                    onClick={addLink}
                    className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                  >
                    Add Link
                  </button>
                  <button
                    onClick={() => {
                      setShowLinkInput(false);
                      setLinkUrl('');
                      setLinkText('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Guest Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900 mb-2">Sending to:</p>
            <div className="flex flex-wrap gap-2">
              {selectedGuests.slice(0, 5).map(guest => (
                <span key={guest.id} className="px-2 py-1 bg-white rounded text-sm text-gray-700">
                  {guest.name}
                </span>
              ))}
              {selectedGuests.length > 5 && (
                <span className="px-2 py-1 bg-white rounded text-sm text-gray-500">
                  +{selectedGuests.length - 5} more
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !message.trim()}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Message
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Media Item Component
const MediaItem = ({ item, onRemove }) => {
  const getIcon = () => {
    switch (item.type) {
      case 'image': return <Image className="w-5 h-5 text-blue-600" />;
      case 'video': return <Video className="w-5 h-5 text-purple-600" />;
      case 'pdf': return <FileText className="w-5 h-5 text-red-600" />;
      case 'link': return <Link className="w-5 h-5 text-green-600" />;
      default: return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        {getIcon()}
        <div>
          <p className="text-sm font-medium text-gray-900">
            {item.text || item.name || 'Attachment'}
          </p>
          {item.size && (
            <p className="text-xs text-gray-500">
              {(item.size / 1024 / 1024).toFixed(2)} MB
            </p>
          )}
        </div>
      </div>
      <button
        onClick={onRemove}
        className="p-1 hover:bg-gray-200 rounded transition"
      >
        <Trash2 className="w-4 h-4 text-red-600" />
      </button>
    </div>
  );
};